import { create } from 'zustand';
import {
  assignShift,
  cancelSingleShift,
  unassignShift,
  deleteSingleShift,
  shareShifts,
  hardDeleteSingleShift,
  requestAssignExternalShift,
} from '@libs/api/shifts';
import { Shift } from '@libs/models/shifts';
import { Analytics } from '@libs/analytics/amplitude';
import { getSingleShift, publishShifts } from '@libs/api/shifts';
import { checkOverlapForAssign } from '@libs/api/shifts/single/checks';

type InitialState = {
  loadingById: Record<string, boolean>;
  currentShift: Shift | null;
  unassignDialogOpen: boolean;
  assignShift: (shiftId: string, userId: string, doubleShiftId?: string | null) => Promise<void>;
  requestAssignShiftOnExternal: (shiftId: string, userId: string) => Promise<void>;
  checkAssignOverlap: (params: { shiftID: string; assigneeId: string }) => Promise<{
    canCreateDoubleShift: true;
    overlappedShiftId: string | null;
  }>;
  unassignShift: (shiftId: string, data: { reason: string; reasonComment: string }) => Promise<void>;
  cancelShift: (shiftId: string, reason: string) => Promise<void>;
  deleteShift: (shiftId: string) => Promise<void>;
  hardDeleteShift: (shiftId: string) => Promise<void>;
  shareShifts: (shiftIds: string[], agenciesIds: string[], allowMultipleAssignees: boolean) => Promise<void>;
  publishShifts: (shiftIds: string[]) => Promise<void>;
  openShiftUnassignDialog: (shiftId: string) => void;
  closeShiftUnassignDialog: () => void;
};

// TODO: rename it to something like `useShiftsActions`
export const useShiftsAssignment = create<InitialState>()((set) => ({
  loadingById: {},
  currentShift: null,
  unassignDialogOpen: false,
  assignShift: async (shiftId: string, userId: string, doubleShiftId?: string | null) => {
    set((data) => ({ loadingById: { ...data.loadingById, [shiftId]: true } }));
    try {
      await assignShift(shiftId, userId, doubleShiftId);
      Analytics.track('Shift: Assigned', {
        shift_id: shiftId,
      });
    } finally {
      set((data) => ({
        loadingById: { ...data.loadingById, [shiftId]: false },
      }));
    }
  },
  requestAssignShiftOnExternal: async (shiftId: string, userId: string) => {
    set((data) => ({ loadingById: { ...data.loadingById, [shiftId]: true } }));
    try {
      await requestAssignExternalShift(shiftId, userId);
      Analytics.track('Shift: RequestAssignOnExternal', {
        shift_id: shiftId,
      });
    } finally {
      set((data) => ({
        loadingById: { ...data.loadingById, [shiftId]: false },
      }));
    }
  },

  checkAssignOverlap: async (params) => {
    const response = await checkOverlapForAssign(params);
    return response;
  },

  unassignShift: async (shiftId: string, data) => {
    set((data) => ({ loadingById: { ...data.loadingById, [shiftId]: true } }));
    try {
      await unassignShift(shiftId, data.reason, data.reasonComment);
      Analytics.track('Shift: Unassigned', {
        shift_id: shiftId,
        unassignment_reason: data.reason,
      });
    } finally {
      set((data) => ({
        loadingById: { ...data.loadingById, [shiftId]: false },
      }));
    }
  },

  cancelShift: async (shiftId: string, reason: string) => {
    await cancelSingleShift(shiftId, reason);
  },

  deleteShift: async (shiftId: string) => {
    await deleteSingleShift(shiftId);
  },

  hardDeleteShift: async (shiftId: string) => {
    await hardDeleteSingleShift(shiftId);
  },

  shareShifts: async (shiftIds: string[], agenciesUrls: string[], allowMultipleAssignees: boolean) => {
    await shareShifts(shiftIds, agenciesUrls, allowMultipleAssignees);
  },

  publishShifts: async (shiftIds: string[]) => {
    await publishShifts(shiftIds);
  },

  openShiftUnassignDialog: async (shiftId) => {
    const shift = await getSingleShift(shiftId);
    set({
      currentShift: shift,
      unassignDialogOpen: true,
    });
  },
  closeShiftUnassignDialog: () => {
    set({ currentShift: null, unassignDialogOpen: false });
  },
}));
